// Appearance for the workspace and dashboard: Light, Dark or System (the default). It's kept in this browser
// (the hub doesn't store it yet) and applied as html[data-theme], which tokens.css reads. Routes that keep their
// own look (sign-in, sign-up, setup, the landing, legal) get no data-theme at all, so they never turn dark.

import { useSyncExternalStore } from 'react';

export type Appearance = 'light' | 'dark' | 'system';
export type Theme = 'light' | 'dark';

const KEY = 'cmx.appearance';
const DARK_MQ = '(prefers-color-scheme: dark)';
const listeners = new Set<() => void>();

function read(): Appearance {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    return 'system';
  }
}

let appearance: Appearance = read();
let themed = false;
const systemDark = () => typeof matchMedia === 'function' && matchMedia(DARK_MQ).matches;

/** The theme in effect: what the person picked, or the computer's when they picked System. */
export function theme(): Theme {
  return appearance === 'system' ? (systemDark() ? 'dark' : 'light') : appearance;
}

/** A theme flip changes colour on nearly every element at once; with their transitions running the page smears
 *  instead of switching. Turn transitions off for the flip, force a style pass, and let them back on the next frame. */
function apply() {
  const root = document.documentElement;
  const next = themed ? theme() : null;
  if ((root.dataset.theme ?? null) === next) return;
  const style = document.createElement('style');
  style.textContent = '*,*::before,*::after{transition:none !important}';
  document.head.appendChild(style);
  if (next) root.dataset.theme = next;
  else delete root.dataset.theme;
  void getComputedStyle(root).color;
  requestAnimationFrame(() => requestAnimationFrame(() => style.remove()));
  listeners.forEach((l) => l());
}

/** Turn the workspace system on or off for the current route (App.tsx). */
export function setThemed(on: boolean) {
  themed = on;
  apply();
}

export function setAppearance(a: Appearance) {
  appearance = a;
  try {
    if (a === 'system') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, a);
  } catch {
    /* private window: it lasts until the tab closes */
  }
  apply();
  listeners.forEach((l) => l());
}

if (typeof matchMedia === 'function') matchMedia(DARK_MQ).addEventListener('change', () => appearance === 'system' && apply());
// another tab changed it
if (typeof window !== 'undefined')
  window.addEventListener('storage', (e) => {
    if (e.key === KEY) {
      appearance = read();
      apply();
      listeners.forEach((l) => l());
    }
  });

const subscribe = (l: () => void) => (listeners.add(l), () => void listeners.delete(l));

export const useAppearance = () => useSyncExternalStore(subscribe, () => appearance);
/** The theme in effect on a themed route; terminals and canvases that draw their own colours follow it. */
export const useTheme = () => useSyncExternalStore(subscribe, theme);
export const onThemeChange = subscribe;
